import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import Marquee from "react-fast-marquee";
import KYCVerification from "@/components/KYCVerification";
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  PieChart,
  Clock,
  DollarSign,
  LogOut,
  LayoutDashboard,
  LineChart,
  Package,
  Settings,
  Plus,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Menu,
  X,
  Gift,
  Copy,
  Check,
  Minus,
  User,
  Mail,
  AlertTriangle,
  Shield,
} from "lucide-react";
import { FaBitcoin, FaEthereum } from "react-icons/fa";
import { SiTether } from "react-icons/si";

const DashboardPage = () => {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [cryptoPrices, setCryptoPrices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [depositAmount, setDepositAmount] = useState("");
  const [depositDialogOpen, setDepositDialogOpen] = useState(false);
  const [depositCryptoType, setDepositCryptoType] = useState("btc");
  const [depositTxHash, setDepositTxHash] = useState("");
  const [cryptoAddresses, setCryptoAddresses] = useState({ btc_address: "", usdt_trc20_address: "" });
  const [copiedAddress, setCopiedAddress] = useState(false);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawMethod, setWithdrawMethod] = useState("btc");
  const [withdrawWalletAddress, setWithdrawWalletAddress] = useState("");
  const [withdrawBankName, setWithdrawBankName] = useState("");
  const [withdrawAccountNumber, setWithdrawAccountNumber] = useState("");
  const [withdrawAccountName, setWithdrawAccountName] = useState("");
  const [withdrawRoutingNumber, setWithdrawRoutingNumber] = useState("");
  const [buyCryptoDialogOpen, setBuyCryptoDialogOpen] = useState(false);
  const [buyCoinId, setBuyCoinId] = useState("bitcoin");
  const [buyAmountUsd, setBuyAmountUsd] = useState("");
  const [buyingCrypto, setBuyingCrypto] = useState(false);
  const [depositBankRef, setDepositBankRef] = useState("");
  const [investDialogOpen, setInvestDialogOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState("");
  const [investAmount, setInvestAmount] = useState("");
  const [copiedCode, setCopiedCode] = useState(false);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [pricesRes, packagesRes, investmentsRes, transactionsRes, referralRes, cryptoRes] = await Promise.all([
        axios.get(`${API}/crypto/prices`),
        axios.get(`${API}/packages`),
        axios.get(`${API}/investments`, { headers, withCredentials: true }),
        axios.get(`${API}/transactions`, { headers, withCredentials: true }),
        axios.get(`${API}/referral/stats`, { headers, withCredentials: true }),
        axios.get(`${API}/settings/crypto-addresses`),
      ]);
      
      setCryptoPrices(pricesRes.data?.slice(0, 10) || []);
      setPackages(packagesRes.data || []);
      setInvestments(investmentsRes.data || []);
      setTransactions(transactionsRes.data || []);
      setReferralStats(referralRes.data || null);
      setCryptoAddresses(cryptoRes.data || { btc_address: "", usdt_trc20_address: "", bank_account: {} });
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Reset state on user change to prevent stale data
    setTransactions([]);
    setInvestments([]);
    setReferralStats(null);
    if (user) {
      fetchData();
    }
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [user?.user_id]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleDeposit = async () => {
    if (!depositAmount || parseFloat(depositAmount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/deposit`,
        { 
          amount: parseFloat(depositAmount),
          crypto_type: depositCryptoType,
          tx_hash: depositTxHash || null,
          bank_reference: depositBankRef || null
        },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      toast.success("Deposit request submitted for approval");
      setDepositAmount("");
      setDepositTxHash("");
      setDepositBankRef("");
      setDepositCryptoType("btc");
      setDepositDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Deposit failed");
    }
  };

  const copyWalletAddress = (address) => {
    navigator.clipboard.writeText(address);
    setCopiedAddress(true);
    toast.success("Address copied to clipboard!");
    setTimeout(() => setCopiedAddress(false), 2000);
  };

  const handleWithdraw = async () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (withdrawMethod !== "bank_transfer" && !withdrawWalletAddress) {
      toast.error("Please enter your wallet address");
      return;
    }
    if (withdrawMethod === "bank_transfer" && (!withdrawBankName || !withdrawAccountNumber || !withdrawAccountName)) {
      toast.error("Please fill in your bank details");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/withdraw`,
        {
          amount: parseFloat(withdrawAmount),
          method: withdrawMethod,
          wallet_address: withdrawMethod !== "bank_transfer" ? withdrawWalletAddress : null,
          bank_name: withdrawMethod === "bank_transfer" ? withdrawBankName : null,
          account_number: withdrawMethod === "bank_transfer" ? withdrawAccountNumber : null,
          account_name: withdrawMethod === "bank_transfer" ? withdrawAccountName : null,
          routing_number: withdrawMethod === "bank_transfer" ? withdrawRoutingNumber : null,
        },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      toast.success("Withdrawal request submitted for approval");
      setWithdrawAmount("");
      setWithdrawWalletAddress("");
      setWithdrawBankName("");
      setWithdrawAccountNumber("");
      setWithdrawAccountName("");
      setWithdrawRoutingNumber("");
      setWithdrawDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Withdrawal failed");
    }
  };

  const handleBuyCrypto = async () => {
    if (!buyAmountUsd || parseFloat(buyAmountUsd) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    setBuyingCrypto(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/buy-crypto`,
        { coin_id: buyCoinId, amount_usd: parseFloat(buyAmountUsd) },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      toast.success(res.data.message);
      setBuyAmountUsd("");
      setBuyCryptoDialogOpen(false);
      fetchData();
      checkAuth();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Purchase failed");
    } finally {
      setBuyingCrypto(false);
    }
  };

  const handleInvest = async () => {
    if (!selectedPackage || !investAmount || parseFloat(investAmount) <= 0) {
      toast.error("Please select a package and enter amount");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/investments`,
        { package_id: selectedPackage, amount: parseFloat(investAmount) },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      toast.success("Investment created successfully!");
      setSelectedPackage("");
      setInvestAmount("");
      setInvestDialogOpen(false);
      fetchData();
      checkAuth();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Investment failed");
    }
  };

  const copyReferralCode = () => {
    if (referralStats?.referral_code) {
      navigator.clipboard.writeText(referralStats.referral_code);
      setCopiedCode(true);
      toast.success("Referral code copied!");
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const totalInvested = investments
    .filter((inv) => inv.status === "active")
    .reduce((sum, inv) => sum + inv.amount, 0);

  const expectedReturns = investments
    .filter((inv) => inv.status === "active")
    .reduce((sum, inv) => sum + inv.expected_return, 0);

  const getCryptoIcon = (symbol) => {
    if (symbol?.toLowerCase() === "btc") return <FaBitcoin className="w-5 h-5 text-amber-500" />;
    if (symbol?.toLowerCase() === "eth") return <FaEthereum className="w-5 h-5 text-blue-500" />;
    return <TrendingUp className="w-5 h-5 text-slate-400" />;
  };

  const navItems = [
    { icon: <LayoutDashboard className="w-5 h-5" />, label: "Dashboard", href: "/dashboard", active: true },
    { icon: <LineChart className="w-5 h-5" />, label: "Trading", href: "/trading" },
    { icon: <Package className="w-5 h-5" />, label: "Packages", href: "/packages" },
    { icon: <User className="w-5 h-5" />, label: "Profile", href: "/profile" },
  ];

  if (user?.role === "admin") {
    navItems.push({ icon: <Settings className="w-5 h-5" />, label: "Admin Panel", href: "/admin" });
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Price Ticker */}
      <div className="ticker-premium">
        <Marquee gradient={false} speed={40} className="py-2">
          <div className="flex gap-8 px-4">
            {cryptoPrices.map((coin) => (
              <div key={coin.id} className="flex items-center gap-2">
                {getCryptoIcon(coin.symbol)}
                <span className="font-medium text-slate-300 uppercase text-sm">{coin.symbol}</span>
                <span className="font-mono text-white text-sm">${coin.current_price?.toLocaleString()}</span>
                <span className={`font-mono text-xs ${coin.price_change_percentage_24h >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                  {coin.price_change_percentage_24h >= 0 ? "+" : ""}{coin.price_change_percentage_24h?.toFixed(2)}%
                </span>
              </div>
            ))}
          </div>
        </Marquee>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`fixed lg:relative inset-y-0 left-0 z-50 w-64 sidebar-premium transform transition-transform lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <Link to="/" className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center shadow-gold">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <span className="font-heading text-lg font-semibold text-platinum">AndInvest</span>
              </Link>
              <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-slate-400 hover:text-platinum">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-2">
              {navItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    item.active
                      ? "nav-link-active"
                      : "text-slate-500 hover:bg-slate-100 hover:text-platinum"
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
                >
                  {item.icon}
                  <span className="font-medium">{item.label}</span>
                </Link>
              ))}
            </nav>

            {/* User Info */}
            <div className="p-4 border-t border-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-100 to-amber-200 rounded-full flex items-center justify-center">
                  <span className="text-amber-700 font-semibold">
                    {user?.name?.charAt(0)?.toUpperCase() || "U"}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-platinum font-medium truncate">{user?.name}</p>
                  <p className="text-slate-400 text-sm truncate">{user?.email}</p>
                </div>
              </div>
              <Button
                variant="outline"
                className="w-full border-slate-300 text-slate-600 hover:bg-slate-100 hover:text-platinum gap-2"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </Button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-h-screen">
          {/* Header */}
          <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 px-6 py-4 sticky top-0 z-40">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-slate-400 hover:text-platinum">
                  <Menu className="w-6 h-6" />
                </button>
                <div>
                  <h1 className="font-heading text-2xl font-semibold text-platinum">Dashboard</h1>
                  <p className="text-slate-500 text-sm">Welcome back, {user?.name?.split(" ")[0]}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Dialog open={withdrawDialogOpen} onOpenChange={setWithdrawDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="border-slate-300 text-slate-600 hover:bg-slate-100 gap-2" data-testid="withdraw-btn">
                      <Minus className="w-4 h-4" />
                      Withdraw
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-white border-slate-200 max-w-md">
                    <DialogHeader>
                      <DialogTitle className="text-platinum font-heading">Request Withdrawal</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4 max-h-[70vh] overflow-y-auto">
                      <div className="p-4 bg-slate-50 rounded-xl">
                        <p className="text-sm text-slate-500">Available Balance</p>
                        <p className="text-2xl font-heading font-semibold text-platinum">
                          ${(user?.balance || 0).toLocaleString()}
                        </p>
                        {referralStats?.can_withdraw && user?.referral_balance > 0 && (
                          <p className="text-sm text-amber-600 mt-1">
                            + ${user.referral_balance.toLocaleString()} referral bonus available
                          </p>
                        )}
                      </div>
                      {/* Withdrawal Method */}
                      <div className="space-y-2">
                        <Label className="text-slate-700">Withdrawal Method</Label>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { id: "btc", label: "BTC", icon: <FaBitcoin className="w-5 h-5 text-amber-500" /> },
                            { id: "usdt_trc20", label: "USDT", icon: <SiTether className="w-5 h-5 text-emerald-500" /> },
                            { id: "bank_transfer", label: "Bank", icon: <DollarSign className="w-5 h-5 text-blue-500" /> },
                          ].map((m) => (
                            <button key={m.id} type="button" onClick={() => setWithdrawMethod(m.id)}
                              className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${withdrawMethod === m.id ? "border-amber-500 bg-amber-50" : "border-slate-200 hover:border-slate-300"}`}
                              data-testid={`withdraw-method-${m.id}`}>
                              {m.icon}
                              <span className="text-xs font-medium text-slate-700">{m.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                      {/* Wallet address for crypto */}
                      {withdrawMethod !== "bank_transfer" && (
                        <div className="space-y-2">
                          <Label className="text-slate-700">Your {withdrawMethod === "btc" ? "BTC" : "USDT TRC20"} Wallet Address</Label>
                          <Input placeholder="Enter your wallet address" value={withdrawWalletAddress} onChange={(e) => setWithdrawWalletAddress(e.target.value)} className="border-slate-300 font-mono text-sm" data-testid="withdraw-wallet-input" />
                        </div>
                      )}
                      {/* Bank details for bank transfer */}
                      {withdrawMethod === "bank_transfer" && (
                        <div className="space-y-3">
                          <div className="space-y-2">
                            <Label className="text-slate-700">Bank Name</Label>
                            <Input value={withdrawBankName} onChange={(e) => setWithdrawBankName(e.target.value)} placeholder="e.g., First Bank" data-testid="withdraw-bank-name" />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-slate-700">Account Name</Label>
                            <Input value={withdrawAccountName} onChange={(e) => setWithdrawAccountName(e.target.value)} placeholder="Your full name" data-testid="withdraw-acct-name" />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-slate-700">Account Number</Label>
                            <Input value={withdrawAccountNumber} onChange={(e) => setWithdrawAccountNumber(e.target.value)} placeholder="1234567890" className="font-mono" data-testid="withdraw-acct-number" />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-slate-700">Routing Number (optional)</Label>
                            <Input value={withdrawRoutingNumber} onChange={(e) => setWithdrawRoutingNumber(e.target.value)} placeholder="021000021" className="font-mono" data-testid="withdraw-routing" />
                          </div>
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label className="text-slate-700">Amount (USD)</Label>
                        <Input type="number" placeholder="Enter amount" value={withdrawAmount} onChange={(e) => setWithdrawAmount(e.target.value)} className="border-slate-300" data-testid="withdraw-amount-input" />
                      </div>
                      {withdrawAmount && parseFloat(withdrawAmount) > 0 && (
                        <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-1" data-testid="vat-preview">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">Gross Amount:</span>
                            <span className="text-slate-800 font-medium">${parseFloat(withdrawAmount).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-rose-600">VAT (7.5%):</span>
                            <span className="text-rose-600 font-medium">-${(parseFloat(withdrawAmount) * 0.075).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                          </div>
                          <div className="border-t border-slate-200 pt-1 flex justify-between text-sm">
                            <span className="text-emerald-700 font-semibold">You Receive:</span>
                            <span className="text-emerald-700 font-semibold">${(parseFloat(withdrawAmount) * 0.925).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                          </div>
                        </div>
                      )}
                      <Button onClick={handleWithdraw} className="w-full btn-premium" data-testid="submit-withdraw-btn">
                        Submit Request
                      </Button>
                      <p className="text-xs text-slate-500 text-center">
                        Withdrawals require admin approval. A 7.5% VAT applies.
                      </p>
                    </div>
                  </DialogContent>
                </Dialog>
                <Dialog open={depositDialogOpen} onOpenChange={setDepositDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="btn-gold gap-2" data-testid="deposit-btn">
                      <Plus className="w-4 h-4" />
                      Deposit
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-white border-slate-200 max-w-md">
                    <DialogHeader>
                      <DialogTitle className="text-platinum font-heading">Deposit Funds</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4 max-h-[70vh] overflow-y-auto">
                      {/* Deposit Method Selection */}
                      <div className="space-y-2">
                        <Label className="text-slate-700">Deposit Method</Label>
                        <div className="grid grid-cols-3 gap-2">
                          <button type="button" onClick={() => setDepositCryptoType("btc")}
                            className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${depositCryptoType === "btc" ? "border-amber-500 bg-amber-50" : "border-slate-200 hover:border-slate-300"}`}
                            data-testid="select-btc">
                            <FaBitcoin className="w-5 h-5 text-amber-500" />
                            <span className="text-xs font-medium text-slate-700">Bitcoin</span>
                          </button>
                          <button type="button" onClick={() => setDepositCryptoType("usdt_trc20")}
                            className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${depositCryptoType === "usdt_trc20" ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}
                            data-testid="select-usdt">
                            <SiTether className="w-5 h-5 text-emerald-500" />
                            <span className="text-xs font-medium text-slate-700">USDT TRC20</span>
                          </button>
                          <button type="button" onClick={() => setDepositCryptoType("bank_transfer")}
                            className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${depositCryptoType === "bank_transfer" ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}
                            data-testid="select-bank-deposit">
                            <DollarSign className="w-5 h-5 text-blue-500" />
                            <span className="text-xs font-medium text-slate-700">Bank Transfer</span>
                          </button>
                        </div>
                      </div>

                      {/* Crypto Wallet Address Display */}
                      {depositCryptoType !== "bank_transfer" && (
                        <div className="space-y-2">
                          <Label className="text-slate-700">
                            Send {depositCryptoType === "btc" ? "BTC" : "USDT (TRC20)"} to this address:
                          </Label>
                          <div className="bg-slate-50 p-3 rounded-xl border border-slate-200">
                            <div className="flex items-center justify-between gap-2">
                              <code className="text-xs font-mono text-slate-700 break-all">
                                {depositCryptoType === "btc"
                                  ? (cryptoAddresses.btc_address || "No BTC address configured")
                                  : (cryptoAddresses.usdt_trc20_address || "No USDT address configured")}
                              </code>
                              {(depositCryptoType === "btc" ? cryptoAddresses.btc_address : cryptoAddresses.usdt_trc20_address) && (
                                <button type="button" onClick={() => copyWalletAddress(depositCryptoType === "btc" ? cryptoAddresses.btc_address : cryptoAddresses.usdt_trc20_address)}
                                  className="p-2 bg-white rounded-lg hover:bg-slate-100 transition-colors flex-shrink-0" data-testid="copy-address-btn">
                                  {copiedAddress ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4 text-slate-500" />}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Bank Account Details Display */}
                      {depositCryptoType === "bank_transfer" && (
                        <div className="space-y-2">
                          <Label className="text-slate-700">Transfer to this bank account:</Label>
                          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-2 text-sm">
                            {cryptoAddresses.bank_account?.bank_name ? (
                              <>
                                <div className="flex justify-between"><span className="text-slate-500">Bank:</span><span className="text-slate-700 font-medium">{cryptoAddresses.bank_account.bank_name}</span></div>
                                <div className="flex justify-between"><span className="text-slate-500">Account Name:</span><span className="text-slate-700 font-medium">{cryptoAddresses.bank_account.account_name}</span></div>
                                <div className="flex justify-between"><span className="text-slate-500">Account Number:</span><span className="text-slate-700 font-mono">{cryptoAddresses.bank_account.account_number}</span></div>
                                {cryptoAddresses.bank_account.routing_number && <div className="flex justify-between"><span className="text-slate-500">Routing:</span><span className="text-slate-700 font-mono">{cryptoAddresses.bank_account.routing_number}</span></div>}
                                {cryptoAddresses.bank_account.swift_code && <div className="flex justify-between"><span className="text-slate-500">SWIFT:</span><span className="text-slate-700 font-mono">{cryptoAddresses.bank_account.swift_code}</span></div>}
                              </>
                            ) : (
                              <p className="text-slate-500 text-center py-2">No bank account configured yet. Contact support.</p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Amount */}
                      <div className="space-y-2">
                        <Label className="text-slate-700">Amount (USD)</Label>
                        <Input type="number" placeholder="Enter amount in USD" value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)} className="border-slate-200" data-testid="deposit-amount-input" />
                      </div>

                      {/* Transaction Hash / Bank Reference */}
                      {depositCryptoType !== "bank_transfer" ? (
                        <div className="space-y-2">
                          <Label className="text-slate-700">Transaction Hash (optional)</Label>
                          <Input placeholder="Enter your transaction hash" value={depositTxHash} onChange={(e) => setDepositTxHash(e.target.value)} className="border-slate-200 font-mono text-sm" data-testid="deposit-txhash-input" />
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Label className="text-slate-700">Bank Reference / Receipt Number</Label>
                          <Input placeholder="Enter bank transfer reference" value={depositBankRef} onChange={(e) => setDepositBankRef(e.target.value)} className="border-slate-200" data-testid="deposit-bank-ref-input" />
                        </div>
                      )}

                      <Button onClick={handleDeposit} className="w-full btn-gold" data-testid="submit-deposit-btn">
                        Submit Deposit Request
                      </Button>
                      <p className="text-xs text-slate-500 text-center">
                        Deposits are credited after admin verification
                      </p>
                    </div>
                  </DialogContent>
                </Dialog>
                {/* Buy Crypto Button */}
                <Dialog open={buyCryptoDialogOpen} onOpenChange={setBuyCryptoDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="border-amber-300 text-amber-600 hover:bg-amber-50 gap-2" data-testid="buy-crypto-btn">
                      <FaBitcoin className="w-4 h-4" />
                      Buy Crypto
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-white border-slate-200 max-w-md">
                    <DialogHeader>
                      <DialogTitle className="text-platinum font-heading">Buy Crypto from Balance</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div className="p-4 bg-slate-50 rounded-xl">
                        <p className="text-sm text-slate-500">Your Balance</p>
                        <p className="text-2xl font-heading font-semibold text-platinum">${(user?.balance || 0).toLocaleString()}</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-700">Select Cryptocurrency</Label>
                        <select value={buyCoinId} onChange={(e) => setBuyCoinId(e.target.value)} className="w-full h-10 px-3 border border-slate-200 rounded-md text-sm" data-testid="buy-coin-select">
                          {cryptoPrices.length > 0 ? cryptoPrices.map((c) => (
                            <option key={c.id} value={c.id}>{c.name} ({c.symbol?.toUpperCase()}) - ${c.current_price?.toLocaleString()}</option>
                          )) : (
                            <>
                              <option value="bitcoin">Bitcoin (BTC)</option>
                              <option value="ethereum">Ethereum (ETH)</option>
                              <option value="tether">Tether (USDT)</option>
                            </>
                          )}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-700">Amount (USD)</Label>
                        <Input type="number" placeholder="Enter USD amount" value={buyAmountUsd} onChange={(e) => setBuyAmountUsd(e.target.value)} className="border-slate-200" data-testid="buy-amount-input" />
                      </div>
                      <Button onClick={handleBuyCrypto} className="w-full btn-gold" disabled={buyingCrypto} data-testid="submit-buy-crypto-btn">
                        {buyingCrypto ? "Processing..." : "Buy Now"}
                      </Button>
                      <p className="text-xs text-slate-500 text-center">
                        Crypto is purchased at current market price and added to your holdings
                      </p>
                    </div>
                  </DialogContent>
                </Dialog>
                <Button variant="outline" className="border-slate-300 text-slate-600 hover:bg-slate-100" onClick={fetchData}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </header>

          {/* Dashboard Content */}
          <div className="p-6">
            {/* Email Verification Alert */}
            {user && !user.email_verified && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between" data-testid="email-verification-alert">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-amber-800 font-medium">Verify your email address</p>
                    <p className="text-amber-700 text-sm">Check your inbox for a verification link, or click resend if you didn't receive it.</p>
                  </div>
                </div>
                <Button
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                  data-testid="resend-verification-btn"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem("token");
                      await axios.post(`${API}/auth/resend-verification`, {}, {
                        headers: { Authorization: `Bearer ${token}` },
                        withCredentials: true
                      });
                      toast.success("Verification email sent! Check your inbox.");
                    } catch (error) {
                      toast.error(error.response?.data?.detail || "Failed to resend. Check SMTP settings in admin.");
                    }
                  }}
                >
                  <RefreshCw className="w-4 h-4 mr-2" /> Resend Email
                </Button>
              </div>
            )}

            {/* Tier Upgrade Prompt - Only show for Tier 1 verified users */}
            {user && user.email_verified && user.tier === 1 && user.kyc_status !== "pending" && user.kyc_status !== "under_review" && (
              <div className="mb-6 p-4 bg-gradient-to-r from-navy-50 to-slate-50 border border-navy-200 rounded-xl flex items-center justify-between" data-testid="tier-upgrade-prompt">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-navy-100 rounded-lg flex items-center justify-center">
                    <Shield className="w-5 h-5 text-navy-600" />
                  </div>
                  <div>
                    <p className="text-navy-700 font-medium">Upgrade to Verified Tier</p>
                    <p className="text-slate-600 text-sm">Complete identity verification to access Gold and Platinum packages.</p>
                  </div>
                </div>
                <Button size="sm" className="btn-gold" onClick={() => document.getElementById('kyc-section')?.scrollIntoView({ behavior: 'smooth' })}>
                  Get Verified
                </Button>
              </div>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="stat-card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-xl flex items-center justify-center">
                      <Wallet className="w-6 h-6 text-emerald-600" />
                    </div>
                    <span className="badge badge-success">Available</span>
                  </div>
                  <p className="text-3xl font-heading font-semibold text-platinum mb-1">
                    ${(user?.balance || 0).toLocaleString()}
                  </p>
                  <p className="text-slate-500 text-sm">Account Balance</p>
                </CardContent>
              </Card>

              <Card className="stat-card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center">
                      <PieChart className="w-6 h-6 text-blue-600" />
                    </div>
                    <span className="badge badge-info">Active</span>
                  </div>
                  <p className="text-3xl font-heading font-semibold text-platinum mb-1">
                    ${totalInvested.toLocaleString()}
                  </p>
                  <p className="text-slate-500 text-sm">Total Invested</p>
                </CardContent>
              </Card>

              <Card className="stat-card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-amber-200 rounded-xl flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-amber-600" />
                    </div>
                    <span className="badge badge-warning">Projected</span>
                  </div>
                  <p className="text-3xl font-heading font-semibold text-platinum mb-1">
                    ${expectedReturns.toLocaleString()}
                  </p>
                  <p className="text-slate-500 text-sm">Expected Returns</p>
                </CardContent>
              </Card>

              <Card className="stat-card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl flex items-center justify-center">
                      <Gift className="w-6 h-6 text-purple-600" />
                    </div>
                    <span className="badge badge-premium">Bonus</span>
                  </div>
                  <p className="text-3xl font-heading font-semibold text-platinum mb-1">
                    ${(user?.referral_balance || 0).toLocaleString()}
                  </p>
                  <p className="text-slate-500 text-sm">Referral Bonus</p>
                </CardContent>
              </Card>
            </div>

            {/* Main Grid */}
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Active Investments */}
              <Card className="lg:col-span-2 bg-white shadow-luxury border-slate-200">
                <CardHeader className="flex flex-row items-center justify-between border-b border-slate-100">
                  <CardTitle className="text-platinum font-heading">Active Investments</CardTitle>
                  <Dialog open={investDialogOpen} onOpenChange={setInvestDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="btn-gold gap-2" data-testid="new-investment-btn">
                        <Plus className="w-4 h-4" />
                        New Investment
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-white border-slate-200">
                      <DialogHeader>
                        <DialogTitle className="text-platinum font-heading">Create Investment</DialogTitle>
                      </DialogHeader>
                      {!user?.email_verified ? (
                        <div className="space-y-4 pt-4 text-center">
                          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                            <Mail className="w-8 h-8 text-amber-600" />
                          </div>
                          <h3 className="text-lg font-semibold text-slate-800">Email Verification Required</h3>
                          <p className="text-slate-500 text-sm">You need to verify your email address before you can invest. Check your inbox for the verification link.</p>
                          <Button onClick={async () => {
                            try {
                              const token = localStorage.getItem("token");
                              await axios.post(`${API}/auth/resend-verification`, {}, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
                              toast.success("Verification email sent! Check your inbox.");
                            } catch (e) { toast.error(e.response?.data?.detail || "Failed to send verification email"); }
                          }} className="w-full btn-gold" data-testid="invest-verify-email-btn">
                            <Mail className="w-4 h-4 mr-2" /> Send Verification Email
                          </Button>
                        </div>
                      ) : (
                      <div className="space-y-4 pt-4">
                        <div className="space-y-2">
                          <Label className="text-slate-700">Select Package</Label>
                          <Select value={selectedPackage} onValueChange={setSelectedPackage}>
                            <SelectTrigger className="border-slate-300" data-testid="package-select">
                              <SelectValue placeholder="Choose a package" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-slate-200">
                              {packages.map((pkg) => (
                                <SelectItem key={pkg.package_id} value={pkg.package_id}>
                                  {pkg.name} - {pkg.roi_percentage}% ROI
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-slate-700">Amount (USD)</Label>
                          <Input
                            type="number"
                            placeholder="Enter investment amount"
                            value={investAmount}
                            onChange={(e) => setInvestAmount(e.target.value)}
                            className="border-slate-300"
                            data-testid="invest-amount-input"
                          />
                        </div>
                        <Button onClick={handleInvest} className="w-full btn-gold" data-testid="submit-investment-btn">
                          Create Investment
                        </Button>
                      </div>
                      )}
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent className="p-6">
                  {investments.filter((i) => i.status === "active").length === 0 ? (
                    <div className="text-center py-12">
                      <PieChart className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">No active investments</p>
                      <p className="text-slate-400 text-sm">Start by creating your first investment</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {investments
                        .filter((i) => i.status === "active")
                        .map((inv) => {
                          const pkg = packages.find((p) => p.package_id === inv.package_id);
                          return (
                            <div
                              key={inv.investment_id}
                              className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100 hover:border-amber-200 transition-colors"
                            >
                              <div className="flex items-center gap-4">
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                                  pkg?.tier === "gold" ? "bg-gradient-to-br from-amber-100 to-amber-200" :
                                  pkg?.tier === "platinum" ? "bg-gradient-to-br from-slate-700 to-slate-800" : 
                                  "bg-gradient-to-br from-slate-100 to-slate-200"
                                }`}>
                                  <DollarSign className={`w-6 h-6 ${
                                    pkg?.tier === "gold" ? "text-amber-600" :
                                    pkg?.tier === "platinum" ? "text-white" : "text-slate-500"
                                  }`} />
                                </div>
                                <div>
                                  <p className="text-platinum font-medium">{pkg?.name || "Unknown"}</p>
                                  <p className="text-slate-500 text-sm">
                                    {inv.roi_percentage}% ROI &bull; Ends {new Date(inv.end_date).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-platinum font-mono font-medium">${inv.amount.toLocaleString()}</p>
                                <p className="text-emerald-600 text-sm font-mono">
                                  +${(inv.expected_return - inv.amount).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Sidebar Cards */}
              <div className="space-y-6">
                {/* Referral Card */}
                <Card className="bg-gradient-to-br from-amber-400 to-amber-500 text-platinum shadow-gold border-0">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Gift className="w-6 h-6" />
                      <h3 className="font-heading text-lg font-semibold">Referral Program</h3>
                    </div>
                    <p className="text-platinum/80 text-sm mb-4">
                      Earn $100 for every friend who invests
                    </p>
                    <div className="bg-white/20 rounded-xl p-4 mb-4">
                      <p className="text-xs text-platinum/70 mb-1">Your Referral Code</p>
                      <div className="flex items-center justify-between">
                        <span className="font-mono font-bold text-lg">{referralStats?.referral_code || "..."}</span>
                        <button 
                          onClick={copyReferralCode}
                          className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                        >
                          {copiedCode ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-platinum/70">Total Referrals</span>
                      <span className="font-semibold">{referralStats?.total_referrals || 0}</span>
                    </div>
                    {!referralStats?.can_withdraw && (user?.referral_balance || 0) > 0 && (
                      <p className="text-xs text-platinum/60 mt-3">
                        *Referral bonus can be withdrawn with an active investment
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* KYC Verification Section */}
                <div id="kyc-section">
                  <KYCVerification user={user} onStatusChange={() => { fetchData(); checkAuth(); }} />
                </div>

                {/* Recent Transactions */}
                <Card className="bg-white shadow-luxury border-slate-200">
                  <CardHeader className="border-b border-slate-100">
                    <CardTitle className="text-platinum font-heading text-lg">Recent Transactions</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    {transactions.length === 0 ? (
                      <div className="text-center py-8">
                        <Clock className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-400 text-sm">No transactions yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {transactions.slice(0, 5).map((txn) => (
                          <div
                            key={txn.transaction_id}
                            className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0"
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                txn.type === "deposit" ? "bg-emerald-100" :
                                txn.type === "withdrawal" ? "bg-rose-100" :
                                txn.type === "referral_bonus" ? "bg-amber-100" : "bg-blue-100"
                              }`}>
                                {txn.type === "deposit" ? (
                                  <ArrowDownRight className="w-4 h-4 text-emerald-600" />
                                ) : txn.type === "withdrawal" ? (
                                  <ArrowUpRight className="w-4 h-4 text-rose-600" />
                                ) : txn.type === "referral_bonus" ? (
                                  <Gift className="w-4 h-4 text-amber-600" />
                                ) : (
                                  <TrendingUp className="w-4 h-4 text-blue-600" />
                                )}
                              </div>
                              <div>
                                <p className="text-platinum text-sm font-medium capitalize">{txn.type.replace("_", " ")}</p>
                                <p className="text-slate-400 text-xs">
                                  {new Date(txn.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className={`font-mono text-sm ${
                                txn.type === "deposit" || txn.type === "referral_bonus" || txn.type === "return" 
                                  ? "text-emerald-600" : "text-platinum"
                              }`}>
                                {txn.type === "deposit" || txn.type === "referral_bonus" || txn.type === "return" ? "+" : "-"}${txn.amount.toLocaleString()}
                              </p>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                txn.status === "completed" ? "badge-success" :
                                txn.status === "pending" ? "badge-warning" :
                                "badge-error"
                              }`}>
                                {txn.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}
    </div>
  );
};

export default DashboardPage;
